import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Phone, PhoneCall, PhoneIncoming, PhoneOutgoing, Voicemail,
  Mic, Settings, Play, CheckCircle, XCircle, Clock, Users,
  AlertCircle, Volume2, PhoneOff, MessageSquare
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VoiceAgentPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, calls, outbound, voicemails, settings
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState(null);
  const [calls, setCalls] = useState([]);
  const [voicemails, setVoicemails] = useState([]);
  const [loading, setLoading] = useState(false);

  // Outbound call form
  const [outboundForm, setOutboundForm] = useState({
    toPhone: '',
    carrierName: '',
    loadId: '',
    message: ''
  });

  useEffect(() => {
    fetchStatus();
    fetchStats();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/voice/status`);
      setStatus(res.data);
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/voice/admin/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCalls = async (direction = null) => {
    setLoading(true);
    try {
      const params = direction ? `?direction=${direction}` : '';
      const res = await axios.get(`${API}/voice/calls${params}`);
      setCalls(res.data.calls || []);
    } catch (error) {
      console.error('Error fetching calls:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVoicemails = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/voice/voicemails`);
      setVoicemails(res.data.voicemails || []);
    } catch (error) {
      console.error('Error fetching voicemails:', error);
    } finally {
      setLoading(false);
    }
  };

  const makeOutboundCall = async () => {
    if (!outboundForm.toPhone) {
      toast.error('Please enter a phone number');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/voice/outbound`, {
        to_phone: outboundForm.toPhone,
        carrier_name: outboundForm.carrierName || undefined,
        load_id: outboundForm.loadId || undefined,
        message: outboundForm.message || undefined
      });

      toast.success(res.data.message);
      setOutboundForm({ toPhone: '', carrierName: '', loadId: '', message: '' });
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate call');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (callStatus) => {
    switch (callStatus) {
      case 'completed': return 'text-green-400 bg-green-500/20';
      case 'answered': return 'text-blue-400 bg-blue-500/20';
      case 'ringing': return 'text-amber-400 bg-amber-500/20';
      case 'initiated': return 'text-purple-400 bg-purple-500/20';
      case 'failed': return 'text-red-400 bg-red-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="p-6 max-w-7xl mx-auto" data-testid="voice-agent-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Phone className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">AI Voice Agent</h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Intelligent voice assistant for inbound and outbound carrier calls
          </p>
        </div>

        {/* Configuration Status */}
        {status && !status.twilio_configured && (
          <Card className="bg-amber-500/10 border-amber-500/30 mb-6">
            <CardContent className="p-4 flex items-center gap-4">
              <AlertCircle className="w-8 h-8 text-amber-400" />
              <div>
                <p className="text-amber-400 font-medium">Twilio Not Configured</p>
                <p className="text-amber-300 text-sm">
                  Add TWILIO_ACCOUNT_SID and TWILIO_AUTH_TOKEN to your environment to enable voice calls.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 justify-center">
          {['dashboard', 'calls', 'outbound', 'voicemails', 'settings'].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'outline'}
              onClick={() => {
                setActiveTab(tab);
                if (tab === 'calls') fetchCalls();
                if (tab === 'voicemails') fetchVoicemails();
              }}
              className={activeTab === tab ? 'bg-violet-500' : ''}
            >
              {tab === 'dashboard' && <Mic className="w-4 h-4 mr-2" />}
              {tab === 'calls' && <PhoneCall className="w-4 h-4 mr-2" />}
              {tab === 'outbound' && <PhoneOutgoing className="w-4 h-4 mr-2" />}
              {tab === 'voicemails' && <Voicemail className="w-4 h-4 mr-2" />}
              {tab === 'settings' && <Settings className="w-4 h-4 mr-2" />}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Status Card */}
            <Card className={`border-2 ${status?.twilio_configured ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/50' : 'bg-slate-800/50 border-slate-700'}`}>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${status?.twilio_configured ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
                    {status?.twilio_configured ? (
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    ) : (
                      <XCircle className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-xl font-bold text-white">
                      Voice Agent {status?.twilio_configured ? 'Active' : 'Inactive'}
                    </p>
                    <p className="text-gray-400">
                      Phone: {status?.phone_number || 'Not configured'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats Grid */}
            {stats && (
              <div className="grid sm:grid-cols-4 gap-4">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4 text-center">
                    <PhoneCall className="w-8 h-8 text-violet-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{stats.calls?.total || 0}</p>
                    <p className="text-sm text-gray-400">Total Calls</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4 text-center">
                    <PhoneIncoming className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{stats.calls?.inbound || 0}</p>
                    <p className="text-sm text-gray-400">Inbound</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4 text-center">
                    <PhoneOutgoing className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{stats.calls?.outbound || 0}</p>
                    <p className="text-sm text-gray-400">Outbound</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4 text-center">
                    <Users className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{stats.responses?.conversion_rate || 0}%</p>
                    <p className="text-sm text-gray-400">Conversion Rate</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Feature Cards */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <PhoneIncoming className="w-5 h-5 text-blue-500" />
                    Inbound Calls
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-gray-400 text-sm">When carriers call your number:</p>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" /> AI-powered IVR menu
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" /> Load search by voice
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" /> Rate quotes
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" /> Voicemail with transcription
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <PhoneOutgoing className="w-5 h-5 text-green-500" />
                    Outbound Calls
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-gray-400 text-sm">Call carriers about loads:</p>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" /> Automated load offers
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" /> Interest tracking
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" /> Do-not-call compliance
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" /> Call status updates
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Calls Tab */}
        {activeTab === 'calls' && (
          <div className="space-y-6">
            {/* Filter Buttons */}
            <div className="flex gap-2">
              <Button onClick={() => fetchCalls()} variant="outline">All Calls</Button>
              <Button onClick={() => fetchCalls('inbound')} variant="outline">
                <PhoneIncoming className="w-4 h-4 mr-2" /> Inbound
              </Button>
              <Button onClick={() => fetchCalls('outbound')} variant="outline">
                <PhoneOutgoing className="w-4 h-4 mr-2" /> Outbound
              </Button>
            </div>

            {/* Calls List */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Call History</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-gray-400 text-center py-8">Loading...</p>
                ) : calls.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No calls yet</p>
                ) : (
                  <div className="space-y-3">
                    {calls.map((call) => (
                      <div key={call.id || call.call_sid} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          {call.direction === 'inbound' ? (
                            <PhoneIncoming className="w-5 h-5 text-blue-400" />
                          ) : (
                            <PhoneOutgoing className="w-5 h-5 text-green-400" />
                          )}
                          <div>
                            <p className="text-white font-medium">
                              {call.direction === 'inbound' ? call.from_phone : call.to_phone}
                            </p>
                            {call.carrier_name && (
                              <p className="text-gray-400 text-sm">{call.carrier_name}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(call.status)}`}>
                              {call.status?.toUpperCase()}
                            </span>
                            {call.duration_seconds && (
                              <p className="text-gray-400 text-xs mt-1">
                                {formatDuration(call.duration_seconds)}
                              </p>
                            )}
                          </div>
                          {call.response && (
                            <span className={`px-2 py-1 rounded text-xs ${
                              call.response === 'interested' ? 'bg-green-500/20 text-green-400' :
                              call.response === 'opted_out' ? 'bg-red-500/20 text-red-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {call.response}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Outbound Tab */}
        {activeTab === 'outbound' && (
          <div className="max-w-xl mx-auto">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <PhoneOutgoing className="w-5 h-5 text-green-500" />
                  Make Outbound Call
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Phone Number *</label>
                  <Input
                    value={outboundForm.toPhone}
                    onChange={(e) => setOutboundForm({...outboundForm, toPhone: e.target.value})}
                    placeholder="+1 555-123-4567"
                    className="bg-slate-700/50 border-slate-600 text-white"
                    data-testid="outbound-phone-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Carrier Name (optional)</label>
                  <Input
                    value={outboundForm.carrierName}
                    onChange={(e) => setOutboundForm({...outboundForm, carrierName: e.target.value})}
                    placeholder="John Smith"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Load ID (optional)</label>
                  <Input
                    value={outboundForm.loadId}
                    onChange={(e) => setOutboundForm({...outboundForm, loadId: e.target.value})}
                    placeholder="Load ID to offer"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Custom Message (optional)</label>
                  <Input
                    value={outboundForm.message}
                    onChange={(e) => setOutboundForm({...outboundForm, message: e.target.value})}
                    placeholder="Custom message for the carrier"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <Button 
                  onClick={makeOutboundCall}
                  disabled={loading || !status?.twilio_configured}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500"
                  data-testid="make-call-btn"
                >
                  {loading ? 'Calling...' : (
                    <>
                      <Phone className="w-4 h-4 mr-2" /> Make Call
                    </>
                  )}
                </Button>
                {!status?.twilio_configured && (
                  <p className="text-amber-400 text-sm text-center">
                    Configure Twilio credentials to enable calling
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Voicemails Tab */}
        {activeTab === 'voicemails' && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Voicemail className="w-5 h-5 text-purple-500" />
                Voicemail Messages
                {stats?.voicemails?.unread > 0 && (
                  <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
                    {stats.voicemails.unread} new
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-gray-400 text-center py-8">Loading...</p>
              ) : voicemails.length === 0 ? (
                <div className="text-center py-8">
                  <Voicemail className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">No voicemails yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {voicemails.map((vm) => (
                    <div key={vm.id} className="p-4 bg-slate-700/30 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Voicemail className="w-5 h-5 text-purple-400" />
                          <span className="text-white font-medium">
                            {new Date(vm.created_at).toLocaleString()}
                          </span>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${
                          vm.status === 'new' ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {vm.status}
                        </span>
                      </div>
                      {vm.transcript && (
                        <p className="text-gray-300 text-sm bg-slate-800/50 p-3 rounded">
                          {vm.transcript}
                        </p>
                      )}
                      {vm.recording_url && (
                        <div className="mt-2">
                          <Button variant="outline" size="sm" asChild>
                            <a href={vm.recording_url} target="_blank" rel="noopener noreferrer">
                              <Play className="w-4 h-4 mr-2" /> Play Recording
                            </a>
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="max-w-xl mx-auto space-y-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-gray-400" />
                  Voice Agent Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-slate-700/30 rounded-lg">
                  <p className="text-gray-400 text-sm mb-2">Phone Number</p>
                  <p className="text-white font-mono">{status?.phone_number || 'Not configured'}</p>
                </div>
                
                <div className="p-4 bg-slate-700/30 rounded-lg">
                  <p className="text-gray-400 text-sm mb-2">Twilio Status</p>
                  <div className="flex items-center gap-2">
                    {status?.twilio_configured ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <span className="text-green-400">Connected</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-5 h-5 text-red-400" />
                        <span className="text-red-400">Not Configured</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="p-4 bg-slate-700/30 rounded-lg">
                  <p className="text-gray-400 text-sm mb-2">AI Status</p>
                  <div className="flex items-center gap-2">
                    {status?.ai_available ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <span className="text-green-400">GPT-5.2 Available</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-5 h-5 text-red-400" />
                        <span className="text-red-400">AI Not Configured</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-blue-400 font-medium mb-2">Setup Instructions</p>
                  <ol className="text-blue-300 text-sm space-y-2 list-decimal list-inside">
                    <li>Create a Twilio account at twilio.com</li>
                    <li>Get your Account SID and Auth Token</li>
                    <li>Purchase or port phone number +1 470-460-8012</li>
                    <li>Add credentials to your environment variables</li>
                    <li>Configure webhook URL to /api/voice/incoming</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VoiceAgentPage;
